// Public Helpdesk client — unauthenticated fetchers for the /p/ pages.
// These hit /public/v1/kb/* (wildcard CORS, no Bearer token) and must never
// touch Firebase auth.

import type { KbTipTapDoc } from '@/lib/real-api';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.usekora.app';

export interface PublicKbBranding {
  accent_color: string | null;
  light_fg: string | null;
  light_bg: string | null;
  dark_fg: string | null;
  dark_bg: string | null;
  title_font: string | null;
  body_font: string | null;
}

export interface PublicKbCenter {
  title: string;
  intro: string | null;
  logo_url: string | null;
  workspace_name: string;
  is_public: boolean;
  subs_enabled: boolean;
  support_url: string | null;
  branding: PublicKbBranding;
  meta: { title: string; description: string | null };
}

export interface PublicKbArticleSummary {
  slug: string;
  title: string;
  summary: string;
  cover_key: string | null;
  published_at: string | null;
  updated_at: string | null;
  helpful_count: number;
  not_helpful_count: number;
  view_count: number;
}

export interface PublicKbArticle extends PublicKbArticleSummary {
  content: KbTipTapDoc;
  meta: { title: string; description: string | null };
  published: boolean;
  unlisted: boolean;
  noindex: boolean;
  url?: string;
  more?: PublicKbArticleSummary[];
}

export interface PublicKbSearchResult {
  slug: string;
  title: string;
  summary: string;
  cover_key: string | null;
  published_at: string | null;
}

export interface PublicKbDirectoryEntry {
  ident: string;
  workspace_id: string;
  name: string;
  title: string;
  intro: string | null;
  logo_url: string | null;
  article_count: number;
  latest_published_at: string | null;
}

async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, init);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error((data as { error?: string }).error || `Request failed (${res.status})`);
    (err as { status?: number }).status = res.status;
    throw err;
  }
  return data as T;
}

/** Resolve a media path (/public/v1/kb/media/…) into a full URL, appending a
 *  signature when the article isn't publicly servable (draft preview). */
export function kbMediaUrl(src: string, assetSigs?: Record<string, string>): string {
  if (src.startsWith('/public/v1/kb/media/')) {
    const key = decodeURIComponent(src.slice('/public/v1/kb/media/'.length).split('?')[0]);
    const sig = assetSigs?.[key];
    return `${API_URL}${src}${sig ? `?${sig}` : ''}`;
  }
  return src;
}

export function fetchPublicCenter(ident: string): Promise<{ ident: string; center: PublicKbCenter; articles: PublicKbArticleSummary[] }> {
  return getJson(`/public/v1/kb/${encodeURIComponent(ident)}`);
}

/** Listing page size: divisible by the 2- and 3-column grid. API max is 50. */
export const PUBLIC_KB_PAGE_SIZE = 24;

export interface PublicKbCenterPage {
  ident: string;
  center: PublicKbCenter;
  articles: PublicKbArticleSummary[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
  has_more: boolean;
}

/** One page of the public listing (newest first). Passing `page` switches
 *  the API into paged mode; without it the API returns the full list. */
export function fetchPublicCenterPage(ident: string, page: number, limit = PUBLIC_KB_PAGE_SIZE): Promise<PublicKbCenterPage> {
  return getJson(`/public/v1/kb/${encodeURIComponent(ident)}?page=${page}&limit=${limit}`);
}

/** Pages 1..`through` merged (deep links like ?page=3 render everything up to
 *  that page, so a crawler following "Load more" sees the cumulative list). */
export async function fetchPublicCenterThrough(ident: string, through: number): Promise<PublicKbCenterPage> {
  const n = Math.min(Math.max(1, Math.floor(through) || 1), 10);
  const pages = await Promise.all(Array.from({ length: n }, (_, i) => fetchPublicCenterPage(ident, i + 1)));
  const last = pages[pages.length - 1];
  const seen = new Set<string>();
  const articles = pages.flatMap((p) => p.articles).filter((a) => (seen.has(a.slug) ? false : (seen.add(a.slug), true)));
  return { ...last, center: pages[0].center, ident: pages[0].ident, articles };
}

export function fetchPublicArticle(ident: string, slug: string): Promise<{ ident: string; center: PublicKbCenter; article: PublicKbArticle }> {
  return getJson(`/public/v1/kb/${encodeURIComponent(ident)}/articles/${encodeURIComponent(slug)}`);
}

export function searchPublicKb(ident: string, q: string): Promise<{ results: PublicKbSearchResult[] }> {
  return getJson(`/public/v1/kb/${encodeURIComponent(ident)}/search?q=${encodeURIComponent(q)}`);
}

export interface KbVoteResult {
  helpful_count: number;
  not_helpful_count: number;
  /** This visitor's authoritative vote after the call: true / false / null (none). */
  my_vote: boolean | null;
}

/** helpful=true|false casts/switches; null removes this visitor's vote. */
export function votePublicKb(ident: string, slug: string, helpful: boolean | null, visitorUuid: string): Promise<KbVoteResult> {
  return getJson(`/public/v1/kb/${encodeURIComponent(ident)}/articles/${encodeURIComponent(slug)}/vote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ helpful, visitor_uuid: visitorUuid }),
  });
}

/** The visitor's recorded vote for an article (restores button state on reload). */
export function getPublicKbVote(ident: string, slug: string, visitorUuid: string): Promise<KbVoteResult> {
  return getJson(`/public/v1/kb/${encodeURIComponent(ident)}/articles/${encodeURIComponent(slug)}/vote?visitor_uuid=${encodeURIComponent(visitorUuid)}`);
}

export function subscribePublicKb(ident: string, email: string): Promise<{ already: boolean }> {
  return getJson(`/public/v1/kb/${encodeURIComponent(ident)}/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

export function fetchKbDirectory(): Promise<{ centers: PublicKbDirectoryEntry[] }> {
  return getJson('/public/v1/kb/directory');
}

export interface KbPreviewPayload {
  preview: true;
  expires_at: string;
  ident: string;
  article: PublicKbArticle;
  asset_sigs: Record<string, string>;
}

export function fetchKbPreview(token: string): Promise<KbPreviewPayload> {
  return getJson(`/public/v1/kb/preview/${encodeURIComponent(token)}`);
}

// Anonymous visitor id (first-party, random, per browser) — used for vote
// dedup and visitor analytics. The API stores only a salted hash.
export function kbVisitorUuid(): string {
  try {
    let uuid = localStorage.getItem('hc-visitor-uuid');
    if (!uuid) {
      uuid = crypto.randomUUID();
      localStorage.setItem('hc-visitor-uuid', uuid);
    }
    return uuid;
  } catch {
    return crypto.randomUUID();
  }
}

export function trackPublicKbView(ident: string, path: string, articleSlug?: string, referrer?: string): void {
  // Fire-and-forget beacon — analytics must never break the page.
  fetch(`${API_URL}/public/v1/kb/${encodeURIComponent(ident)}/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      path,
      article_slug: articleSlug,
      visitor_uuid: kbVisitorUuid(),
      referrer: referrer || undefined,
    }),
  }).catch(() => {});
}

// ── TipTap doc media hydration (editor display ↔ stored bare paths) ──────────
// Stored docs carry bare media paths (/public/v1/kb/media/kb/…). The editor and
// draft previews need absolute URLs (plus a signature while the article isn't
// publicly servable), so docs are hydrated on load and dehydrated on save.

import type { KbTipTapDoc as Doc } from '@/lib/real-api';

const MEDIA_PATH = '/public/v1/kb/media/';

export function hydrateDocMedia(doc: Doc, sigs?: Record<string, string>): Doc {
  const walk = (node: Doc): Doc => {
    if (node.attrs && typeof node.attrs.src === 'string' && node.attrs.src.startsWith(MEDIA_PATH)) {
      const bare = node.attrs.src.split('?')[0];
      let key = '';
      try { key = decodeURIComponent(bare.slice(MEDIA_PATH.length)); } catch { key = bare.slice(MEDIA_PATH.length); }
      const sig = sigs?.[key];
      node.attrs = { ...node.attrs, src: `${API_URL}${bare}${sig ? `?${sig}` : ''}` };
    }
    if (node.content) node.content = node.content.map(walk);
    return node;
  };
  return walk(structuredClone(doc));
}

export function dehydrateDocMedia(doc: Doc): Doc {
  const walk = (node: Doc): Doc => {
    if (node.attrs && typeof node.attrs.src === 'string') {
      const src: string = node.attrs.src;
      const idx = src.indexOf(MEDIA_PATH);
      if (idx !== -1) {
        node.attrs = { ...node.attrs, src: src.slice(idx).split('?')[0] };
      }
    }
    if (node.content) node.content = node.content.map(walk);
    return node;
  };
  return walk(structuredClone(doc));
}
